import { Campaign } from "@/types/campaign";
import {
  AvailableProvider,
  Inputs,
  Integration,
  IntegrationConnectionData,
  ProviderCapabilities,
  ProviderConfigurationMetadata,
  ProviderConnectionInfo,
  ProviderConnectionInfoField,
  ProviderConnectionInfoFieldMapping,
  ProviderErrorDescription,
  ProviderErrorEnum,
  SettingsSection,
} from "@/types/integrations";
import { LassoErrorCode } from "@/types/lasso-exception";
import { api, LassoException, queryClient } from "@/utils/api";
import {
  getIntegrationConfig,
  getStorageConfig,
} from "@/utils/integration-helpers";
import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import ky, { HTTPError } from "ky";
import { DeepPartial } from "react-hook-form";

export const getAvailableIntegrationsQueryOptions = () =>
  queryOptions({
    queryKey: ["integrations", "available"],
    queryFn: async () => {
      const integrations = await api
        .get("apps/settings/integrations/available")
        .json<Integration[]>();
      return integrations.filter((i) =>
        getIntegrationConfig().allowedIntegrations.includes(i.uniqueName),
      );
    },
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });

export const getAvailableProvidersQueryOptions = (onlyConfigured = false) =>
  queryOptions({
    queryKey: ["providers", "available", onlyConfigured],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      queryParams.set("onlyConfigured", onlyConfigured ? "true" : "false");
      const providers = await api
        .get(`providers/available?${queryParams.toString()}`)
        .json<AvailableProvider[]>();
      return providers.filter((i) =>
        getStorageConfig().allowedProviders.includes(i.uniqueName),
      );
    },
  });

export const useAvailableProvidersQuery = (onlyConfigured = false) => {
  return useQuery(getAvailableProvidersQueryOptions(onlyConfigured));
};

export const getProviderCapabilitiesQueryOptions = (provider: string) =>
  queryOptions({
    queryKey: ["providers", "capabilities", provider],
    queryFn: async () =>
      await api
        .get(`providers/v2/metadata/${provider}/capabilities`)
        .json<ProviderCapabilities>(),
    enabled: !!provider,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

export const useProviderCapabilitiesQuery = (provider: string) => {
  return useQuery(getProviderCapabilitiesQueryOptions(provider));
};

export const useAvailableIntegrationsQuery = () => {
  return useQuery(getAvailableIntegrationsQueryOptions());
};

export const getIntegrationsQueryOptions = () =>
  queryOptions({
    queryKey: ["integrations"],
    queryFn: async () => {
      const integrations = await api
        .get("organizations/me/integrations")
        .json<ProviderConnectionInfo[]>();
      return integrations.filter((i) =>
        getIntegrationConfig().allowedIntegrations.includes(i.provider),
      );
    },
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });

export const useIntegrationsQuery = () => {
  return useQuery(getIntegrationsQueryOptions());
};

export const getIntegrationQueryOptions = (integrationName: string) =>
  queryOptions({
    queryKey: ["integration", integrationName],
    queryFn: async () => {
      const integration = await api
        .get(`organizations/me/integrations/${integrationName}`)
        .json<ProviderConnectionInfo>();
      return integration;
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });

export const useIntegrationQuery = (integrationName: string) => {
  return useQuery(getIntegrationQueryOptions(integrationName));
};

export const getIntegrationConfigurationMetadataQueryOptions = (
  integrationName: string,
) =>
  queryOptions({
    queryKey: ["integration-metadata", integrationName],
    queryFn: async () => {
      const data = await api
        .get(`providers/v2/metadata/${integrationName}/configurationmetadata`)
        .json<ProviderConfigurationMetadata[]>();
      return data;
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });

export const useIntegrationConfigurationMetadata = (
  integrationName: string,
) => {
  return useQuery(
    getIntegrationConfigurationMetadataQueryOptions(integrationName),
  );
};

export const useDeleteIntegrationMutation = () =>
  useMutation({
    mutationFn: async (integrationName: string) => {
      return await api
        .delete(`organizations/me/integrations/${integrationName}`)
        .json<ProviderConnectionInfo>();
    },
    onSuccess: (_, variables, __) => {
      queryClient.removeQueries({
        predicate: (query) => query.queryKey.includes(variables),
      });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes("integrations"),
        type: "all",
      });
    },
  });

export const testIntegrationConnectionMutation = {
  mutationFn: async ({
    integrationName,
    data,
  }: {
    integrationName: string;
    data: IntegrationConnectionData;
  }) => {
    return await api
      .post(`providers/v2/${integrationName}/test`, {
        json: data,
      })
      .json();
  },
};

export const useConnectIntegrationMutation = (
  method: "post" | "put" = "post",
) => {
  return useMutation({
    mutationKey: ["connect-integration", method],
    mutationFn: async ({
      integration,
      data,
    }: {
      integration: string;
      data: DeepPartial<ProviderConnectionInfo> | null;
    }) => {
      const response = await api[method](
        method === "put"
          ? `organizations/me/integrations/${integration}`
          : "organizations/me/integrations",
        { json: data },
      ).json();
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes(integration),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["integrations"],
      });
      return response;
    },
  });
};

export const getIntegrationFieldsQueryOptions = (
  integrationName: string,
  connectionInfo?: ProviderConnectionInfo,
) =>
  queryOptions({
    queryKey: ["integration", "fields", integrationName, connectionInfo],
    queryFn: async () => {
      if (!connectionInfo) {
        const fields = await api
          .get(
            `organizations/me/integrations/${integrationName}/fields?mode=Sales`,
          )
          .json<ProviderConnectionInfoField[]>();
        return fields;
      }
      const fields = await api
        .post(
          `organizations/me/integrations/${integrationName}/fields?mode=Sales`,
          {
            json: connectionInfo,
          },
        )
        .json<ProviderConnectionInfoField[]>();
      return fields;
    },
    enabled: !!integrationName,
  });

export const useAvailableIntegrationFieldsQuery = (
  integrationName: string,
  connectionInfo?: ProviderConnectionInfo,
) => {
  return useQuery(
    getIntegrationFieldsQueryOptions(integrationName, connectionInfo),
  );
};

export const getIntegrationConnectionInfoQueryOptions = (
  integrationName?: string | null,
) =>
  queryOptions({
    queryKey: ["integration", "connection-info", integrationName],
    queryFn: async () => {
      const connectionInfo = await api
        .get(`organizations/me/integrations/${integrationName}`)
        .json<ProviderConnectionInfo>();
      return connectionInfo;
    },
    enabled: !!integrationName,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });

export const useIntegrationConnectionInfoQuery = (
  integrationName?: string | null,
) => {
  return useQuery(getIntegrationConnectionInfoQueryOptions(integrationName));
};

export const useIntegrationConnectionInfoMutation = (
  integrationName: string,
  method: "post" | "put" = "post",
) => {
  return useMutation({
    mutationFn: async ({
      data,
    }: {
      data: Partial<ProviderConnectionInfo> | null;
    }) => {
      const response: ProviderConnectionInfo = await api[method](
        `organizations/me/integrations${
          method === "put" ? "/" + integrationName : ""
        }`,
        {
          json: data,
        },
      ).json();
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["integration", "connection-info", integrationName],
      });
      queryClient.invalidateQueries({
        queryKey: ["integration-metadata", integrationName],
      });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes("integrations"),
      });
    },
  });
};

export const getIntegrationSettingsQueryOptions = (integratioName: string) =>
  queryOptions({
    queryKey: ["integration", "settings", integratioName],
    queryFn: async () => {
      try {
        const settings = await api
          .get(
            `organizations/me/integrations/${integratioName}/settings?mode=Sales`,
          )
          .json<SettingsSection[]>();
        return settings;
      } catch (error: unknown) {
        if (error instanceof HTTPError) {
          if (error.response.status === 400) {
            const body = await error.response.json<LassoException>();
            if (body.errorCode === 6) return [];
          }
          throw error;
        }
      }
    },
    enabled: !!integratioName,
  });

export const useIntegrationSettingsQuery = (integrationName: string) =>
  useQuery(getIntegrationSettingsQueryOptions(integrationName));

export const getIntegrationDefaultMappingsQueryOptions = (
  integrationName: string,
  connectionInfo: ProviderConnectionInfo | undefined,
) =>
  queryOptions({
    queryKey: ["integration", "defaultmappings", integrationName],
    queryFn: async () => {
      const mappings = await api
        .post(
          `organizations/me/integrations/${integrationName}/defaultmappings?mode=Sales`,
          {
            json: connectionInfo,
          },
        )
        .json<ProviderConnectionInfoFieldMapping[]>();
      return mappings;
    },
    enabled: !!integrationName && !!connectionInfo,
  });

export const useIntegrationDefaultMappingsQuery = (
  integrationName: string,
  conntectionInfo: ProviderConnectionInfo,
) =>
  useQuery(
    getIntegrationDefaultMappingsQueryOptions(integrationName, conntectionInfo),
  );

export const getIntegrationErrorDescriptionsQueryOptions = (
  integrationName: string,
  connectionInfo?: ProviderConnectionInfo,
) =>
  queryOptions({
    queryKey: [
      "integration",
      "errordescriptions",
      integrationName,
      connectionInfo,
    ],
    queryFn: async () => {
      if (!connectionInfo) {
        const errorDescriptions = await api
          .get(
            `organizations/me/integrations/${integrationName}/errordescriptions`,
          )
          .json<Record<ProviderErrorEnum, ProviderErrorDescription>>();
        return errorDescriptions;
      }
      const errorDescriptions = await api
        .post(
          `organizations/me/integrations/${integrationName}/errordescriptions`,
          {
            json: connectionInfo,
          },
        )
        .json<Record<ProviderErrorEnum, ProviderErrorDescription>>();
      return errorDescriptions;
    },
    enabled: !!integrationName,
  });

export const useIntegrationErrorDescriptionsQuery = (
  integrationName: string,
  connectionInfo?: ProviderConnectionInfo,
) => {
  return useQuery(
    getIntegrationErrorDescriptionsQueryOptions(
      integrationName,
      connectionInfo,
    ),
  );
};

export const getIntegrationInputQueryOptions = (
  integrationName?: string,
  exportMode?: Campaign["exportMode"],
) =>
  queryOptions({
    queryKey: ["integration", "input", integrationName, exportMode],
    queryFn: async () => {
      const input = await api
        .get(`apps/sales/integrations/input/${integrationName}/${exportMode}`, {
          hooks: {
            beforeRetry: [
              async ({ error }) => {
                if (error instanceof HTTPError) {
                  if (error.response.status === 401) {
                    const body = await error.response.json<LassoException>();
                    if (body.errorMessage?.includes("connection info")) {
                      return ky.stop;
                    }
                  }
                }
              },
            ],
          },
        })
        .json<Inputs>()
        .catch(async (reason) => {
          if (reason instanceof HTTPError && reason.response.status === 401) {
            const body = await reason.response.json<LassoException>();
            if (body.errorCode === LassoErrorCode.AuthenticationError) {
              throw new Error("authenticationError");
            }
          }
          throw reason;
        });
      return input;
    },
    enabled: !!integrationName && !!exportMode,
    staleTime: Infinity,
    gcTime: Infinity,
  });

export const useIntegrationInputQuery = (
  integrationName?: string,
  exportMode?: Campaign["exportMode"],
) => {
  return useQuery(getIntegrationInputQueryOptions(integrationName, exportMode));
};
